import { useState, useCallback, useRef } from 'react';
import * as XLSX from 'xlsx';
import { toast } from 'react-hot-toast';
import { Upload, FileSpreadsheet, CheckCircle2, AlertCircle, RotateCw, Settings2 } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { cn } from '@/shared/utils/utils';
import { PayrollFieldMappingModal } from './FieldMappingModal';
import { usePayrollFieldMapping, useSaveFieldMapping, usePreviewExcel, useUploadPayrollExcel } from './api';
import type { FieldMappingEntry, ExcelPreviewRow } from './api';
import { format } from 'date-fns';

function fmtCurrency(n: number) {
  return `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
}

export function UploadTab() {
  const [monthYear, setMonthYear] = useState(format(new Date(), 'yyyy-MM'));
  const [dragOver, setDragOver] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [detectedHeaders, setDetectedHeaders] = useState<string[]>([]);
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [previewRows, setPreviewRows] = useState<ExcelPreviewRow[] | null>(null);
  const [previewTotal, setPreviewTotal] = useState(0);
  const [uploadResult, setUploadResult] = useState<{ created: number; skipped: number; errors: string[] } | null>(null);
  const [pendingMappings, setPendingMappings] = useState<FieldMappingEntry[] | null>(null);
  const [overwrite, setOverwrite] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: savedMapping } = usePayrollFieldMapping();
  const saveMapping = useSaveFieldMapping();
  const previewMutation = usePreviewExcel();
  const uploadMutation = useUploadPayrollExcel();

  const readHeaders = useCallback((file: File): Promise<string[]> => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const wb = XLSX.read(data, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' }) as string[][];
        const headers = (raw[0] || []).map(h => String(h).trim()).filter(Boolean);
        resolve(headers);
      };
      reader.readAsArrayBuffer(file);
    });
  }, []);

  const handleFile = useCallback(async (file: File) => {
    if (!file.name.match(/\.(xlsx|xls)$/i)) {
      toast.error('Please upload an .xlsx or .xls file');
      return;
    }
    setSelectedFile(file);
    setPreviewRows(null);
    setUploadResult(null);

    const headers = await readHeaders(file);
    setDetectedHeaders(headers);

    // Check if we have a saved mapping that covers these columns
    const hasSavedMapping = savedMapping?.mappings && savedMapping.mappings.length > 0;
    if (hasSavedMapping) {
      // Auto-preview with saved mapping
      try {
        const result = await previewMutation.mutateAsync({ file, mappings: savedMapping!.mappings });
        setPreviewRows(result.preview);
        setPreviewTotal(result.total_rows);
        setPendingMappings(savedMapping!.mappings);
      } catch {
        // If preview fails, show mapping modal
        setShowMappingModal(true);
      }
    } else {
      setShowMappingModal(true);
    }
  }, [savedMapping, previewMutation, readHeaders]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const handleMappingSave = useCallback(async (mappings: FieldMappingEntry[]) => {
    if (!mappings || mappings.length === 0) {
      toast.error('No mappings to save');
      return;
    }
    try {
      await saveMapping.mutateAsync(mappings);
      setPendingMappings(mappings);
      setShowMappingModal(false);

      if (selectedFile) {
        try {
          const result = await previewMutation.mutateAsync({ file: selectedFile, mappings });
          setPreviewRows(result.preview);
          setPreviewTotal(result.total_rows);
          toast.success('Column mapping saved!');
        } catch (err: unknown) {
          toast.error('Mapping saved, but preview failed to load');
        }
      } else {
        toast.success('Column mapping saved!');
      }
    } catch (err: unknown) {
      toast.error('Failed to save mapping');
    }
  }, [saveMapping, previewMutation, selectedFile]);

  const handleConfirmUpload = useCallback(async () => {
    if (!selectedFile || !pendingMappings) return;
    try {
      const result = await uploadMutation.mutateAsync({
        file: selectedFile,
        month_year: monthYear,
        mappings: pendingMappings,
        overwrite,
      });
      setUploadResult({ created: result.created, skipped: result.skipped, errors: result.errors });
      if (result.created > 0) toast.success(`${result.created} payslip(s) created!`);
      if (result.errors.length) toast.error(`${result.errors.length} error(s) — see details below`);
    } catch (err: unknown) {
      toast.error('Upload failed');
    }
  }, [selectedFile, pendingMappings, monthYear, overwrite, uploadMutation]);

  const reset = () => {
    setSelectedFile(null);
    setPreviewRows(null);
    setPreviewTotal(0);
    setUploadResult(null);
    setPendingMappings(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header + month picker */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-on-surface">Upload Payroll Sheet</h3>
          <p className="text-sm text-on-surface-variant mt-0.5">
            Upload your monthly Excel payroll sheet to generate draft payslips.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Input type="month" value={monthYear} onChange={e => setMonthYear(e.target.value)} className="w-40" />
          <label className="flex items-center gap-1.5 text-xs text-on-surface-variant" title="Replace draft payslips that already exist for this month. Finalized payslips are never replaced.">
            <input type="checkbox" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} /> Replace drafts
          </label>
          {savedMapping?.mappings?.length ? (
            <Button variant="outline" size="sm" onClick={() => setShowMappingModal(true)}>
              <Settings2 className="w-3.5 h-3.5" /> Edit Mapping
            </Button>
          ) : null}
        </div>
      </div>

      {/* Drop zone */}
      {!selectedFile && (
        <div
          className={cn(
            'border-2 border-dashed rounded-xl p-10 text-center transition-all cursor-pointer select-none',
            dragOver ? 'border-primary bg-primary/5' : 'border-outline hover:border-primary/60 hover:bg-surface-variant/50'
          )}
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <FileSpreadsheet className="w-12 h-12 mx-auto mb-3 text-primary/60" />
          <p className="font-medium text-on-surface">Drop your Excel file here</p>
          <p className="text-sm text-on-surface-variant mt-1">or click to browse &nbsp;·&nbsp; .xlsx / .xls only</p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            className="hidden"
            onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
          />
        </div>
      )}

      {/* File selected + loading */}
      {selectedFile && !previewRows && !uploadResult && (
        <div className="flex items-center gap-3 p-4 rounded-lg bg-surface-variant border border-outline">
          <FileSpreadsheet className="w-5 h-5 text-primary flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-on-surface truncate">{selectedFile.name}</p>
            <p className="text-xs text-on-surface-variant">Reading file…</p>
          </div>
          <Button variant="outline" size="sm" onClick={reset}>Remove</Button>
        </div>
      )}

      {/* Preview table */}
      {previewRows && !uploadResult && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-on-surface-variant">
              <FileSpreadsheet className="w-4 h-4 text-primary" />
              <span className="font-medium text-on-surface">{selectedFile?.name}</span>
              <span>·</span>
              <span>
                {previewTotal} employee row(s) parsed
                {previewTotal > previewRows.length && ` (showing first ${previewRows.length})`}
              </span>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={reset}>
                <RotateCw className="w-3.5 h-3.5" /> Change File
              </Button>
              <Button variant="outline" size="sm" onClick={() => setShowMappingModal(true)}>
                <Settings2 className="w-3.5 h-3.5" /> Re-map
              </Button>
            </div>
          </div>

          <div className="rounded-lg border border-outline overflow-hidden">
            <div className="overflow-x-auto max-h-72 overflow-y-auto">
              <table className="w-full text-sm text-left">
                <thead className="bg-surface-variant text-on-surface-variant sticky top-0 text-xs uppercase">
                  <tr>
                    <th className="px-4 py-2.5 font-medium">Employee</th>
                    <th className="px-4 py-2.5 font-medium">Designation</th>
                    <th className="px-4 py-2.5 font-medium">Earnings</th>
                    <th className="px-4 py-2.5 font-medium">Deductions</th>
                    <th className="px-4 py-2.5 font-medium text-right">Net Salary</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-outline/40">
                  {previewRows.map((row, i) => (
                    <tr key={i} className="hover:bg-surface-variant/40">
                      <td className="px-4 py-3">
                        <div className="font-medium text-on-surface">{row.employee_name}</div>
                        <div className="text-xs text-on-surface-variant">{row.email}</div>
                      </td>
                      <td className="px-4 py-3 text-on-surface-variant text-xs">{row.designation}</td>
                      <td className="px-4 py-3">
                        <div className="text-xs space-y-0.5">
                          {row.earnings.slice(0, 3).map((e: ExcelPreviewRow['earnings'][number], j: number) => (
                            <div key={j} className="flex justify-between gap-4">
                              <span className="text-on-surface-variant">{e.label}</span>
                              <span className="font-mono text-emerald-600">{fmtCurrency(e.amount)}</span>
                            </div>
                          ))}
                          {row.earnings.length > 3 && <div className="text-on-surface-variant">+{row.earnings.length - 3} more</div>}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-xs space-y-0.5">
                          {row.deductions.slice(0, 3).map((d: ExcelPreviewRow['deductions'][number], j: number) => (
                            <div key={j} className="flex justify-between gap-4">
                              <span className="text-on-surface-variant">{d.label}</span>
                              <span className="font-mono text-rose-600">{fmtCurrency(d.amount)}</span>
                            </div>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right font-semibold font-mono text-emerald-600">
                        {fmtCurrency(row.net_salary)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={reset}>Cancel</Button>
            <Button onClick={handleConfirmUpload} loading={uploadMutation.isPending}>
              <Upload className="w-4 h-4" />
              Generate {previewTotal} Draft Payslip{previewTotal !== 1 ? 's' : ''} for {monthYear}
            </Button>
          </div>
        </div>
      )}

      {/* Result */}
      {uploadResult && (
        <div className="space-y-4">
          <div className="rounded-lg border border-outline p-5 space-y-3">
            <div className="flex items-center gap-2 text-emerald-600 font-semibold">
              <CheckCircle2 className="w-5 h-5" />
              Upload Complete
            </div>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="rounded-lg bg-emerald-50 dark:bg-emerald-900/20 p-3">
                <div className="text-2xl font-bold text-emerald-600">{uploadResult.created}</div>
                <div className="text-xs text-emerald-700 dark:text-emerald-400 mt-1">Payslips Created</div>
              </div>
              <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 p-3">
                <div className="text-2xl font-bold text-amber-600">{uploadResult.skipped}</div>
                <div className="text-xs text-amber-700 dark:text-amber-400 mt-1">Skipped (exist)</div>
              </div>
              <div className="rounded-lg bg-rose-50 dark:bg-rose-900/20 p-3">
                <div className="text-2xl font-bold text-rose-600">{uploadResult.errors.length}</div>
                <div className="text-xs text-rose-700 dark:text-rose-400 mt-1">Errors</div>
              </div>
            </div>
            {uploadResult.errors.length > 0 && (
              <div className="rounded-lg bg-rose-50 dark:bg-rose-900/20 p-3 space-y-1">
                {uploadResult.errors.map((e, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm text-rose-700 dark:text-rose-400">
                    <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                    {e}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="flex justify-end gap-3">
            <Button onClick={reset}>
              <Upload className="w-4 h-4" /> Upload Another Sheet
            </Button>
          </div>
        </div>
      )}

      {/* Field Mapping Modal */}
      <PayrollFieldMappingModal
        open={showMappingModal}
        headers={detectedHeaders}
        initialMappings={savedMapping?.mappings}
        onSave={handleMappingSave}
        onCancel={() => { setShowMappingModal(false); if (!selectedFile) reset(); }}
        saving={saveMapping.isPending || previewMutation.isPending}
      />
    </div>
  );
}
